import { restDefaultHandlers } from '@hotjar/webapp/utils/rest-default-handlers';
import * as recordingsWidget from '@hotjar/webapp/features/Activation/hooks/useRecordingsWidget';
import { cappedLocaleNumber } from '../../RecordingsList/utils/cappedLocaleNumber';
import { showDeleteRecordingsModal } from '../../RecordingsList/DeleteRecordingsModal';
import { sendRecordingsBulkDeletion } from '../api/sendRecordingsBulkDeletion';
import { sendRecordingsQueryDeletion } from '../api/sendRecordingsQueryDeletion';
import { deselectAllRecordings } from './deselectAllRecordings';
import { getPlaybacks } from './getPlaybacks';
import { filter, some } from 'lodash-es';

import type { Site } from '@hotjar/site';
import type { Store } from '../../../store/list/types';

/**
 * Delete a list of recordings (after showing a confirmation).
 */
export const deleteRecordings = (store: Store): Promise<boolean> => {
  const TOTAL_HITS_UPPER_BOUND = window.settings?.recordings?.total_hits_upper_bound || 10000;

  const {
    site,
    jsonFilters,
    hotjarFilters,
    selectedRecordings,
    selectedAllRecordings,
    totalHits,
    totalRecordingsCount,
    recordings: recordingsFromState,
    setSelectedRecordings,
    setSelectedAllRecordings,
    setRecordings,
    setTotalHits,
    setTotalRecordingsCount,
  } = store.getState();

  return new Promise((resolve) => {
    showDeleteRecordingsModal(
      selectedRecordings.length,
      selectedAllRecordings,
      cappedLocaleNumber(totalHits, TOTAL_HITS_UPPER_BOUND) as number,
      async () => {
        if (selectedAllRecordings) {
          sendRecordingsQueryDeletion(jsonFilters, hotjarFilters)
            .then((success) => {
              if (success) {
                deselectAllRecordings(store);
                setSelectedAllRecordings(false);
                setRecordings([]);
                setTotalHits(0);

                window.dispatchEvent(
                  new CustomEvent('hjNotification', {
                    detail: {
                      cls: 'notification-box-info',
                      message: 'Recordings queued for deletion',
                      timeout: 6000,
                    },
                  }),
                );

                resolve(true);
              } else {
                window.dispatchEvent(
                  new CustomEvent('hjNotification', {
                    detail: {
                      cls: 'notification-box-error',
                      message: 'Could not delete recordings',
                      timeout: 6000,
                    },
                  }),
                );

                resolve(false);
              }
            })
            .catch((res) => {
              restDefaultHandlers.onError(res);
              resolve(false);
            });
        } else {
          sendRecordingsBulkDeletion(selectedRecordings)
            .then(async (success) => {
              if (success) {
                const newTotalRecordingsCount = totalRecordingsCount - selectedRecordings.length;

                setSelectedRecordings([]);
                setRecordings(
                  filter(recordingsFromState, function (recording) {
                    return !some(selectedRecordings, {
                      id: recording.id,
                      created: recording.created_epoch_time,
                    });
                  }),
                );
                setTotalRecordingsCount(newTotalRecordingsCount);

                // If total hits is exact, then subtract, but don't bother otherwise.
                if (totalHits < TOTAL_HITS_UPPER_BOUND) {
                  const newTotalHits = totalHits - selectedRecordings.length;
                  setTotalHits(newTotalHits);
                }
                if (totalHits > totalRecordingsCount) {
                  setTotalHits(totalRecordingsCount);
                }

                window.dispatchEvent(
                  new CustomEvent('hjNotification', {
                    detail: {
                      cls: 'notification-box-info',
                      message: 'Recordings deleted',
                      timeout: 6000,
                    },
                  }),
                );

                recordingsWidget.clearRecordingsWidgetData((site as Site).id);
                setTimeout(() => getPlaybacks(store), 0);

                resolve(true);
              } else {
                window.dispatchEvent(
                  new CustomEvent('hjNotification', {
                    detail: {
                      cls: 'notification-box-error',
                      message: 'Could not delete recordings',
                      timeout: 6000,
                    },
                  }),
                );

                resolve(false);
              }
            })
            .catch((res) => {
              restDefaultHandlers.onError(res);
              resolve(false);
            });
        }
      },
    );
  });
};
